import { AppDispatch } from "../redux/store";
import {
  setDrivers,
  setLoading,
  setError,
} from "../features/driverMap/driverMapSlice";
import { fetch4 } from "../commonApis/commonApis";

const url = process.env.REACT_APP_BASE_URL;

const mapboxLocationCache = new Map<string, string>();
let isFetching = false;
let lastFetchTime = 0;
const MIN_FETCH_INTERVAL = 1000;

export const fetchInitialDriverData = async (dispatch: AppDispatch) => {
  //   console.log(" Starting driver data fetch...");

  // Prevent duplicate calls
  const now = Date.now();
  if (isFetching) {
    // console.log("⏳ Driver data fetch already in progress, skipping...");
    return;
  }

  if (now - lastFetchTime < MIN_FETCH_INTERVAL) {
    console.log(
      `Too soon to fetch again (last fetch ${Math.round(
        (now - lastFetchTime) / 1000
      )}s ago), skipping...`
    );
    return;
  }

  isFetching = true;
  lastFetchTime = now;

  try {
    dispatch(setLoading(true));

    const result = await fetch4(`${url}/api/driver/current-locations`, {});

    if (!result || !result.success) {
      const errorMsg =
        result?.error?.message ||
        result?.message ||
        result?.error ||
        "Failed to fetch driver data";
      console.error("❌ API Error:", errorMsg);
      throw new Error(errorMsg);
    }

    if (!result.data) {
      console.log("No driver data available from server");
      dispatch(setDrivers([]));
      return;
    }

    const driversArray = Array.isArray(result.data)
      ? result.data
      : [result.data];

    // Process drivers with reverse geocoding
    const processDriversWithLocation = async () => {
      const transformedDrivers = await Promise.all(
        driversArray.map(async (driver: any) => {
          const lat = parseFloat(driver.latitude || driver.lat);
          const lon = parseFloat(driver.longitude || driver.lon);

          // Only show drivers with valid coordinates
          if (isNaN(lat) || isNaN(lon) || lat === 0 || lon === 0) {
            console.warn(
              "Skipping driver with invalid coordinates:",
              driver.name,
              { lat, lon }
            );
            return null;
          }

          // Get location name from coordinates
          let currentLocation = `Lat: ${lat.toFixed(4)}, Lon: ${lon.toFixed(4)}`;
          const cacheKey = `${lat.toFixed(3)},${lon.toFixed(3)}`;

          if (mapboxLocationCache.has(cacheKey)) {
            currentLocation = mapboxLocationCache.get(cacheKey)!;
          } else if (
            driver.currentLocation &&
            driver.currentLocation !== "Unknown" &&
            !/^\d+\.\d+/.test(driver.currentLocation)
          ) {
            currentLocation = driver.currentLocation;
            mapboxLocationCache.set(cacheKey, currentLocation);
          } else {
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 2000);
              const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=pk.eyJ1IjoiZ3dhcm0wMDciLCJhIjoiY21kcGt6Z3o3MGQ5ejJ3c2RmcXFza3pkMyJ9.na-zjLbQsMMlaGuNnXEuSA&types=place,locality,neighborhood`,
                { signal: controller.signal }
              );
              clearTimeout(timeoutId);

              if (response.ok) {
                const data = await response.json();
                if (data.features && data.features.length > 0) {
                  const feature = data.features[0];
                  let locationName = feature.place_name;

                  // Try to get a shorter, more readable name
                  if (feature.context) {
                    const place = feature.context.find((c: any) =>
                      c.id.startsWith("place")
                    );
                    const locality = feature.context.find((c: any) =>
                      c.id.startsWith("locality")
                    );

                    if (place) {
                      locationName = place.text;
                    } else if (locality) {
                      locationName = locality.text;
                    } else if (feature.text) {
                      locationName = feature.text;
                    }
                  } else if (feature.text) {
                    locationName = feature.text;
                  }

                  currentLocation = locationName;
                  mapboxLocationCache.set(cacheKey, currentLocation);
                } else {
                  mapboxLocationCache.set(cacheKey, currentLocation);
                }
              } else {
                mapboxLocationCache.set(cacheKey, currentLocation);
              }
            } catch (error) {
              // Cache fallback so we do not retry immediately
              mapboxLocationCache.set(cacheKey, currentLocation);
            }
          }

          const rawImg = driver?.image;
          const validImg =
            typeof rawImg === "string" &&
            (rawImg.startsWith("http://") || rawImg.startsWith("https://") || rawImg.startsWith("data:"))
              ? rawImg
              : "";

          return {
            ...driver,
            id: driver?.id || String(Math.random()),
            name: driver?.name || "Unknown Driver",
            code: driver?.code || "",
            image: validImg,
            latitude: String(lat),
            longitude: String(lon),
            lat,
            lon,
            online: driver?.online !== false, // Default to true if not specified
            freshnessStatus: driver?.freshnessStatus || (driver?.online ? "LIVE" : "OFFLINE"),
            lastSeen: driver?.lastSeen || null,
            currentLocation,
            // New fields from API with correct parsing
            totalShipments: parseInt(driver?.totalShipments?.toString() || "0"),
            outForDelivery: parseInt(driver?.outForDelivery?.toString() || "0"),
            ordersDelivered: parseInt(
              driver?.ordersDelivered?.toString() || "0"
            ),
            ordersNotDelivered: parseInt(
              driver?.ordersNotDelivered?.toString() || "0"
            ),
          };
        })
      );

      return transformedDrivers.filter((driver: any) => driver !== null);
    };

    const transformedDrivers = await processDriversWithLocation();

    dispatch(setDrivers(transformedDrivers));
    // console.log(
    //   "Driver data loaded successfully:",
    //   transformedDrivers.length,
    //   "drivers with valid coordinates"
    // );

    transformedDrivers.forEach((driver: any, index: number) => {
      //   console.log(`Driver ${index + 1}:`, {
      //     name: driver?.name,
      //     coordinates: `${driver?.lat}, ${driver?.lon}`,
      //     online: driver?.online,
      //   });
    });
  } catch (err: any) {
    let errorMessage = "Failed to fetch driver data";

    if (typeof err === "string") {
      errorMessage = err;
    } else if (err instanceof Error) {
      errorMessage = err.message;
    } else if (err?.message) {
      errorMessage = err.message;
    } else if (typeof err === "object") {
      errorMessage = JSON.stringify(err);
    }

    console.error("Error fetching initial driver data:", errorMessage);

    if (errorMessage?.toLowerCase().includes("not logged in")) {
      dispatch(setError("Please log in to view driver locations"));
    } else if (
      errorMessage.includes("500") ||
      errorMessage?.toLowerCase().includes("server error")
    ) {
      dispatch(setError("Server error: Unable to load driver locations"));
    } else if (errorMessage.includes("404")) {
      dispatch(setError("Driver location endpoint not found"));
    } else {
      dispatch(setError(errorMessage));
    }

    dispatch(setDrivers([]));
  } finally {
    dispatch(setLoading(false));
    isFetching = false;
  }
};
